import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Package,
  DollarSign,
  Settings,
  Link as LinkIcon,
  LogOut,
  User,
  HelpCircle,
  X,
  MessageSquare,
} from "react-feather";
import logo from "../../assets/logo.svg";
import { useAgencyAuth } from "../../contexts/AgencyAuthContext";
import { Newspaper } from "lucide-react";
import { ThemeToggle } from "../ThemeToggle";
import { useState, useEffect } from "react";

interface MenuItem {
  label: string;
  path: string;
  icon: JSX.Element;
  isActive?: boolean;
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

function MenuItem({
  label,
  path,
  icon,
  isActive,
  onClose,
}: MenuItem & { onClose?: () => void }) {
  const location = useLocation();
  const isCurrentPath = location.pathname === path;

  const handleClick = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <Link
      to={path}
      onClick={handleClick}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
        isActive || isCurrentPath
          ? "text-brand-600"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <span
        className={
          isActive || isCurrentPath ? "text-brand-600" : "text-muted-foreground"
        }
      >
        {icon}
      </span>
      <span>{label}</span>
    </Link>
  );
}

const menuItems = [
  {
    label: "Dashboard",
    path: "/agency/dashboard",
    icon: <Home className="w-5 h-5" />,
  },
  {
    label: "Blogs",
    path: "/agency/blogs",
    icon: <Newspaper className="w-5 h-5" />,
  },
  {
    label: "Brands",
    path: "/agency/brands",
    icon: <Package className="w-5 h-5" />,
  },
  {
    label: "Links",
    path: "/agency/links",
    icon: <LinkIcon className="w-5 h-5" />,
  },
  {
    label: "Payouts",
    path: "/agency/payouts",
    icon: <DollarSign className="w-5 h-5" />,
  },
];

const bottomMenuItems = [
  {
    label: "FAQ",
    path: "/agency/faq",
    icon: <HelpCircle className="w-5 h-5" />,
  },
  {
    label: "Support",
    id: "support",
    path: "/agency/support",
    icon: <MessageSquare className="w-5 h-5" />,
  },
  {
    label: "Settings",
    path: "/agency/settings",
    icon: <Settings className="w-5 h-5" />,
  },
];

export default function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const { user, profile, logout } = useAgencyAuth();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleLogout = async () => {
    try {
      logout();
      navigate("/agency/auth/login");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const sidebarContent = (
    <div className="h-screen w-64 bg-card border-r border-border flex flex-col">
      {/* Header with close button for mobile */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
            <img src={logo} alt="Reveshare" className="w-5 h-5 dark:invert" />
          </div>
          <span className="font-semibold text-foreground">Reveshare</span>
        </div>
        {isMobile && onClose && (
          <button
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-muted-foreground"
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Main menu items */}
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <nav className="px-2 py-4 space-y-1">
          {menuItems.map((item) => (
            <MenuItem key={item.path} {...item} onClose={onClose} />
          ))}
        </nav>
      </div>

      {/* Bottom items - Settings and Profile */}
      <div className="border-t border-border">
        {/* Settings */}
        <nav className="px-2 py-4 space-y-1">
          {bottomMenuItems.map((item) => (
            <MenuItem key={item.path} {...item} onClose={onClose} />
          ))}
        </nav>

        {/* Company info */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden">
              {profile?.profilePicture ? (
                <img
                  src={profile.profilePicture}
                  alt={`${user?.firstName} ${user?.lastName}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-foreground truncate">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="text-sm text-muted-foreground truncate">
                {user?.email || ""}
              </div>
              {user?.code && (
                <div className="text-xs text-brand-600 font-medium">
                  Code: {user.code}
                </div>
              )}
            </div>
            <ThemeToggle className="h-8 w-8" />
            <button
              onClick={handleLogout}
              className="text-muted-foreground hover:text-muted-foreground"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={handleBackdropClick}
          />
        )}

        {/* Sidebar */}
        <div
          className={`fixed top-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {sidebarContent}
        </div>
      </>
    );
  }

  // Desktop version
  return <div className="sticky top-0 left-0">{sidebarContent}</div>;
}
